import { useAppTheme } from "@hooks/useAppTheme";
import type { Media } from "@memorang/types/src/session/new-session";
import {
	Box,
	ImageViewerModal,
	MarkdownRenderer,
	VideoPlayer,
} from "@memorang/ui";
import CustomTabs from "components/CustomTabs";
import { Image } from "expo-image";
import { useMemo, useState } from "react";
import { Platform, Pressable } from "react-native";
import { Caption, Icon } from "react-native-paper";

interface Props {
	media?: Media[];
}

const TranscriptBlock = ({
	transcript,
	defaultExpanded,
	instruction,
	buttonText,
}: {
	transcript: string;
	defaultExpanded?: boolean;
	instruction?: string;
	buttonText?: string;
}) => {
	const theme = useAppTheme();
	const [expanded, setExpanded] = useState(!!defaultExpanded);
	const transcriptMarkdown = useMemo(
		() =>
			transcript
				.split(/\r?\n/)
				.map((line) => line.trim())
				.filter(Boolean)
				.join("\n\n"),
		[transcript],
	);
	const label =
		buttonText ?? (expanded ? "Hide transcript" : "Show transcript");
	const normalizedInstruction = instruction
		?.replace(/\s+/g, " ")
		.trim()
		.toLowerCase();
	const shouldShowInstruction =
		!!instruction &&
		!normalizedInstruction?.startsWith(
			"select the following expandable text button to access the transcript",
		);
	return (
		<Box gap={6}>
			{shouldShowInstruction ? (
				<MarkdownRenderer text={instruction ?? ""} fontSize={16} />
			) : null}
			<Box
				borderWidth={1}
				borderColor={theme.colors.outlineVariant}
				padding={16}
				gap={12}
			>
				<Pressable
					onPress={() => setExpanded((p) => !p)}
					style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
					accessibilityRole="button"
					accessibilityState={{ expanded }}
					accessibilityLabel={label}
				>
					<Icon
						source={expanded ? "chevron-down" : "chevron-right"}
						size={18}
						color={theme.colors.onSurface}
					/>
					<Caption
						style={{
							color: theme.colors.onSurface,
							fontSize: 14,
							fontWeight: "500",
						}}
					>
						{label}
					</Caption>
				</Pressable>
				{expanded && (
					<MarkdownRenderer
						text={transcriptMarkdown}
						fontSize={14}
						paragraphMarginVertical={0}
						customStyles={{
							text: {
								lineHeight: 24,
							},
							paragraph: {
								lineHeight: 24,
								marginTop: 0,
								marginBottom: 8,
							},
						}}
					/>
				)}
			</Box>
		</Box>
	);
};

const VideoPanel = ({ video }: { video: Media }) => (
	<Box gap={12} paddingTop={12} width="100%">
		{video.url ? (
			<Box overflow="hidden" width="100%">
				<VideoPlayer videoUrl={video.url} aspectRatio="16 / 9" />
			</Box>
		) : null}
		{video.transcript ? (
			<TranscriptBlock
				transcript={video.transcript}
				defaultExpanded={false}
				instruction={video.instruction}
				buttonText={video.buttonText}
			/>
		) : null}
	</Box>
);

const ImagePanel = ({ image }: { image: Media }) => {
	const theme = useAppTheme();
	const [aspectRatio, setAspectRatio] = useState<number>();
	const [viewerVisible, setViewerVisible] = useState(false);
	return (
		<Box
			alignSelf="center"
			width="100%"
			maxWidth={420}
			borderWidth={1}
			borderColor={theme.colors.outlineVariant}
			borderRadius={theme.roundness}
			overflow="hidden"
		>
			<Pressable
				onPress={() => setViewerVisible(true)}
				accessibilityRole="imagebutton"
				accessibilityLabel={image.title}
				//@ts-expect-error - only cursor pointer and auto is supported in types
				style={Platform.OS === "web" ? { cursor: "zoom-in" } : undefined}
			>
				<Image
					source={{ uri: image.url }}
					contentFit="contain"
					onLoad={(event) => {
						const { width, height } = event.source ?? {};
						if (width && height) {
							setAspectRatio(width / height);
						}
					}}
					style={{ width: "100%", aspectRatio: aspectRatio ?? 3 / 4 }}
					accessibilityLabel={image.title}
				/>
			</Pressable>
			<ImageViewerModal
				visible={viewerVisible}
				images={[{ url: image.url, caption: image.title }]}
				handleClose={() => setViewerVisible(false)}
			/>
		</Box>
	);
};

const Scenario = ({ media }: Props) => {
	const images = (media ?? []).filter(
		(m) => m.type === "image" || m.type === "IMAGE",
	);
	const videos = (media ?? []).filter(
		(m) => m.type === "video" || m.type === "VIDEO",
	);
	const videoSetKey = videos
		.map((video) => video.url || video.title || "")
		.join("|");
	const [visitedTabState, setVisitedTabState] = useState<{
		videoSetKey: string;
		indexes: Set<number>;
	}>(() => ({
		videoSetKey,
		indexes: new Set([0]),
	}));
	const visitedTabIndexes =
		visitedTabState.videoSetKey === videoSetKey
			? visitedTabState.indexes
			: new Set([0]);

	const markTabVisited = (index: number) => {
		setVisitedTabState((previous) => {
			const previousIndexes =
				previous.videoSetKey === videoSetKey ? previous.indexes : new Set([0]);
			if (previous.videoSetKey === videoSetKey && previousIndexes.has(index)) {
				return previous;
			}
			const next = new Set(previousIndexes);
			next.add(index);
			return {
				videoSetKey,
				indexes: next,
			};
		});
	};

	if (videos.length === 0 && images.length === 0) {
		return null;
	}

	const imageBlock = images.length ? (
		<Box gap={16}>
			{images.map((image, index) => (
				<ImagePanel key={image.id ?? image.url ?? index} image={image} />
			))}
		</Box>
	) : null;

	const isMulti = videos.length > 1;

	const videoBlock = videos.length ? (
		isMulti ? (
			<CustomTabs
				key={videoSetKey}
				tabs={videos.map((video, index) => ({
					label: video.title || `Perspective ${index + 1}`,
					badge: visitedTabIndexes.has(index) ? undefined : true,
					content: (
						<Box paddingTop={16}>
							<VideoPanel video={video} />
						</Box>
					),
				}))}
				mode="fixed"
				onChangeIndex={markTabVisited}
			/>
		) : (
			<VideoPanel video={videos[0]} />
		)
	) : null;

	return (
		<Box gap={16}>
			{imageBlock}
			{videoBlock}
		</Box>
	);
};

export default Scenario;
